import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';
import { getSortedErrors, getVisibleSections } from '@redux/selectors/FormSelector';

import Nav from 'react-bootstrap/Nav';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Card from 'react-bootstrap/Card';
import Collapse from 'react-bootstrap/Collapse';
import Swal from 'sweetalert2';
import GAEventTracking from '@components/tracking/GAEventTracking';

import ValidationFeedback from '@components/smartAdminUI/ValidationFeedback';
import SectionItem from './SectionItem';

const SectionNav = ({
  sectionsPerRepeatableSection,
  sections,
  errPerSection,
  reqPerSection,
}) => {
  const visibleSections = useSelector(getVisibleSections);
  const errors = useSelector(getSortedErrors);
  const sortedErrors = useSelector(getSortedErrors);
  const [open, setOpen] = useState(false);
  const gaEventTracker = GAEventTracking('Tool');

  const displayCopiedMessage = () => Swal.fire({
    position: 'center',
    title: 'Validation Summary Copied',
    icon: 'success',
    iconColor: 'var(--theme-success)',
    showConfirmButton: false,
    timer: 3000,
  });

  const copyToClipboard = () => {
    displayCopiedMessage();
    gaEventTracker('Copy', 'Copied Validation Summary');
    navigator.clipboard.writeText(document.getElementById('validationList').innerText);
  };

  const handleValidationSummaryClick = () => {
    document.getElementById('validationSummary').classList.add('flip-vertical-left');
    document.getElementById('validationSummaryCard').classList.add('flip-vertical-right');
    document.getElementById('validationSummary').classList.remove('d-none');
    document.getElementById('sections').classList.add('d-none');
  };
  const handleSectionsClick = () => {
    document.getElementById('sections').classList.add('flip-vertical-right');
    document.getElementById('sectionsCard').classList.add('flip-vertical-left');
    document.getElementById('validationSummary').classList.add('d-none');
    document.getElementById('sections').classList.remove('d-none');
  };

  return (
    <Nav className="tab-nav flex-column">
      <Card id="sections">
        <div id="sectionsCard">
          <Card.Header>
            <Button
              name="btnValidationSummary"
              variant="primary"
              className="btn-block text-left mb-3"
              onClick={handleValidationSummaryClick}
            >
              {errors.length > 0
                ? (
                  <Badge
                    variant="danger"
                    className="mr-2 width-1"
                  >
                    {errors.length}
                  </Badge>
                )
                : (
                  <span className="fa-stack mr-2 width-1">
                    <i className="fas fa-square fa-stack-2x text-transparent" />
                    <i className="fas fa-check-double text-success fa-stack-1x" />
                  </span>
                )}
              Validation Summary
              <i className="far fa-angle-double-right float-right mt-1" />
            </Button>
            <div className="d-block d-lg-none">
              <Button
                name="btnSections"
                onClick={() => setOpen(!open)}
                aria-controls="collapse-section-nav"
                aria-expanded={open}
                className="mb-2"
                size="sm"
              >
                Sections
                <i className={open ? 'far fa-chevron-up ml-3' : 'far fa-chevron-down ml-3'} />
              </Button>
              <Collapse in={open}>
                <div id="collapse-section-nav">
                  {sections && sections.map(section => (
                    (visibleSections?.find(vSec => vSec === section.sectionName))
                      ? (
                        <SectionItem
                          section={section}
                          key={section.sectionName}
                          sectionsPerRepeatableSection={sectionsPerRepeatableSection}
                          errPerSection={errPerSection}
                          reqPerSection={reqPerSection}
                        />
                      )
                      : null
                  ))}
                </div>
              </Collapse>
            </div>
            <div className="d-none d-lg-block">
              {sections && sections.map(section => (
                (visibleSections?.find(vSec => vSec === section.sectionName))
                  ? (
                    <SectionItem
                      section={section}
                      key={section.sectionName}
                      sectionsPerRepeatableSection={sectionsPerRepeatableSection}
                      errPerSection={errPerSection}
                      reqPerSection={reqPerSection}
                    />
                  )
                  : null
              ))}
            </div>
          </Card.Header>
        </div>
      </Card>
      <Card id="validationSummary" className="d-none">
        <div id="validationSummaryCard">
          <Card.Header>
            <Button
              name="btnSections"
              variant="primary"
              className="btn-block text-left mb-3"
              onClick={handleSectionsClick}
            >
              Sections
              <i className="far fa-angle-double-left float-right mt-1" />
            </Button>
            <div className="d-block d-lg-none">
              <div className="clearfix">
                <Button
                  name="btnAccordionSectionsValidation"
                  onClick={() => setOpen(!open)}
                  aria-controls="collapse-section-validation"
                  aria-expanded={open}
                  className="mb-2"
                  size="sm"
                >
                  {errors.length > 0
                    ? (
                      <Badge
                        variant="danger"
                        className="mr-2 width-1"
                      >
                        {errors.length}
                      </Badge>
                    )
                    : (
                      <span className="fa-stack mr-2 width-1">
                        <i className="fas fa-square fa-stack-2x text-transparent" />
                        <i className="fas fa-check-double text-success fa-stack-1x" />
                      </span>
                    )}
                  Validation Summary
                  <i className={open ? 'far fa-chevron-up ml-3' : 'far fa-chevron-down ml-3'} />
                </Button>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-copy-to-clipboard-button" style={{ margin: 0 }}>Copy to Clipboard</Tooltip>}
                >
                  <Button
                    name="btnCopyToClipboard"
                    className="btn-sm float-right mr-2 mb-2"
                    onClick={copyToClipboard}
                  >
                    <i className="fas fa-clipboard" />
                  </Button>
                </OverlayTrigger>
              </div>
              <Collapse in={open}>
                <div id="collapse-section-validation">
                  <Card.Header className="vh-50">
                    <ValidationFeedback errors={sortedErrors} />
                    {sortedErrors.length === 0 && <p className="pl-3 pt-3">Congratulations, the form is valid.</p>}
                  </Card.Header>
                </div>
              </Collapse>
            </div>
            <div className="d-none d-lg-block">
              <div className="clearfix">
                {errors.length > 0
                  ? (
                    <Badge
                      variant="danger"
                      className="ml-3 mr-2 width-1"
                    >
                      {errors.length}
                    </Badge>
                  )
                  : (
                    <span className="fa-stack ml-3 mr-2 width-1">
                      <i className="fas fa-square fa-stack-2x text-transparent" />
                      <i className="fas fa-check-double text-success fa-stack-1x" />
                    </span>
                  )}
                Validation Summary
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-copy-to-clipboard-button" style={{ margin: 0 }}>Copy to Clipboard</Tooltip>}
                >
                  <Button
                    name="btnCopyToClipboard"
                    className="btn-sm float-right mr-2 mb-2"
                    onClick={copyToClipboard}
                  >
                    <i className="fas fa-clipboard" />
                  </Button>
                </OverlayTrigger>
              </div>
              <Card.Header className="vh-50">
                <ValidationFeedback errors={sortedErrors} />
                {sortedErrors.length === 0 && <p className="pl-3 pt-3">Congratulations, the form is valid.</p>}
              </Card.Header>
            </div>
          </Card.Header>
        </div>
      </Card>
    </Nav>
  );
};

SectionNav.propTypes = {
  sectionsPerRepeatableSection: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.bool)),
  sections: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  errPerSection: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]))).isRequired,
  reqPerSection: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
};

export default SectionNav;
